import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { keyBy } from 'lodash';
import { X } from 'lucide-react';
import { z } from 'zod';

import type { SitePreview } from '@/api/fe-api/domain/entities/site';
import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import { MultiSelect } from '@/components/form/inputs/multi-select';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';
import type { MissingDocument } from '@/modules/documents/domain/entities/document';
import {
  DocumentType,
  DocumentTypeSchema,
} from '@/modules/documents/domain/entities/document-type';
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
} from '@/modules/documents/infra/repositories/fe-api/fe-api.document.repository';
import { CreateDocument } from '@/modules/documents/view/mutations/create-document';
import { formatMB } from '@/utils/format/bytes';
import { toastResult } from '@/utils/toast';

import { FileUploadBox } from './file-upload-box';

export type DocumentUploadDialogProps = {
  template?: MissingDocument;
  siteId?: string;
} & Omit<DialogProps, 'children'>;

enum FormMode {
  DEFAULT = 'default',
  MISSING = 'missing',
}

export function DocumentUploadDialog({
  template,
  open,
  siteId,
  onClose,
}: DocumentUploadDialogProps) {
  const defaultValues: Partial<FormCreateInputs> = useMemo(
    () =>
      template
        ? // Create from a missing document
          {
            ...template,
            tags: template.tags.join(', '),
            siteIds: siteId ? [siteId] : [],
            isGlobal: !siteId,
          }
        : // Create a document from scratch
          {
            siteIds: siteId ? [siteId] : [],
            isGlobal: !siteId,
          },
    [template, siteId],
  );

  const form = useForm<FormCreateInputs>({
    defaultValues,
    resolver: zodResolver(formCreateSchema),
  });

  const { isValid } = form.formState;

  const siteList = GetAllSites.useQuery();

  const siteIdMapper = useMemo<Record<string, SitePreview>>(
    () => keyBy(siteList.data ?? [], 'id'),
    [siteList.data],
  );

  const createDocument = CreateDocument.useMutation();
  const mode = template ? FormMode.MISSING : FormMode.DEFAULT;

  async function handleOnCreate({ siteIds, ...document }: FormCreateInputs) {
    const creation = createDocument.mutateAsync({
      siteIds,
      document: {
        ...document,
        tags:
          document.tags
            ?.split(',')
            .map((tag) => tag.trim())
            .filter(Boolean) ?? [],
      },
    });
    const result = await toastResult(creation, {
      success: `Document "${document.name}" uploaded successfully`,
      loading: 'Uploading document...',
      error(err) {
        switch (err.type) {
          case 'FILE_TOO_BIG': {
            return `The file "${err.filename}" is too large, please choose a file smaller than ${formatMB(err.limit)}`;
          }
          case 'FILE_INVALID_TYPE': {
            return `File of type ${err.given} isn't accepted`;
          }
          case 'EXTERNAL_ERROR': {
            return err.description ?? 'Failed to upload document';
          }
        }
      },
      exception: 'Failed to upload document',
    });

    if (result.isOk()) {
      onClose(true);
    }
  }

  function updateValue(field: keyof FormCreateInputs, value: any) {
    if (value) {
      form.setValue(field, value);
    } else {
      form.resetField(field);
    }
    form.trigger();
  }

  function handleOnChangeFile(file: File | null) {
    updateValue('file', file);
    if (file && form.getValues('name') === '' && mode !== FormMode.MISSING) {
      form.setValue('name', file.name);
    }
  }

  useEffect(() => {
    form.reset(defaultValues);
  }, [form, defaultValues]);

  return (
    <Dialog size="sm" className="my-auto w-96" onClose={onClose} open={open}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleOnCreate)}>
          <DialogTitle>{content[mode].title}</DialogTitle>
          <DialogDescription>{content[mode].description}</DialogDescription>
          <DialogBody className="space-y-4">
            <FormField
              control={form.control}
              name="file"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <FileUploadBox
                      name={field.name}
                      onBlur={field.onBlur}
                      disabled={field.disabled}
                      onChange={handleOnChangeFile}
                      accept={ALLOWED_FILE_TYPES.join()}
                      error={fieldState.error?.message}
                      fileName={field.value?.name ?? ''}
                      hint={`Maximum file size: ${MAX_FILE_SIZE_MB} MB`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>File name</FormLabel>
                  <FormControl>
                    <Input disabled={mode === FormMode.MISSING} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!form.watch('isGlobal') ? (
              <FormField
                control={form.control}
                name="siteIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Associated sites</FormLabel>
                    <FormControl>
                      <MultiSelect<string>
                        className="h-10 ring-neutral-200"
                        placeholder="All sites"
                        value={field.value}
                        options={Object.values(siteIdMapper)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((s) => s.id)}
                        renderValue={(v) =>
                          v
                            .map((siteId) => siteIdMapper[siteId]?.name)
                            .join(', ')
                        }
                        renderOption={(v) => siteIdMapper[v]?.name}
                        onChange={field.onChange}
                        loading={siteList.isFetching}
                        rightIcon={
                          field.value.length > 0 ? (
                            <X
                              className="pointer-events-auto cursor-pointer text-gray-400"
                              onClick={() => field.onChange([])}
                            />
                          ) : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        {Object.values(DocumentType).map((type) => (
                          <SelectItem key={type} value={type.toString()}>
                            {type.toString()}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags (optional)</FormLabel>
                  <FormControl>
                    <Input disabled={mode === FormMode.MISSING} {...field} />
                  </FormControl>
                  <FormDescription>
                    Tags are separated by a comma (,)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>
          <DialogActions>
            <Button
              type="submit"
              disabled={!isValid || createDocument.isLoading}
            >
              Save document
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}

const content = {
  [FormMode.DEFAULT]: {
    title: 'New Document',
    description: 'Add a new document.',
  },
  [FormMode.MISSING]: {
    title: 'Upload Missing Document',
    description:
      'ACCURE is missing the corresponding document. Please upload it here.',
  },
};

const formCreateSchema = z.object({
  name: z.string().min(1),
  siteIds: z.array(z.string()),
  isGlobal: z.boolean(),
  type: DocumentTypeSchema,
  tags: z.string().optional(),
  file: z
    .instanceof(File, { message: 'Required field' })
    .refine((file) => {
      return !file || file.size <= MAX_FILE_SIZE;
    }, `File size must be less than ${MAX_FILE_SIZE_MB} MB`)
    .refine((file) => {
      return ALLOWED_FILE_TYPES.includes(file.type);
    }, 'File type not supported'),
});

type FormCreateInputs = z.infer<typeof formCreateSchema>;
